import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import "./AdminAccountingPercentuali.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// helper: normalizza stringa percentuale
function toStrPercent(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(n);
}

// helper: da cents a stringa euro
function toStrEurFromCents(cents) {
  if (cents === null || cents === undefined) return "";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2);
}

export default function AdminAccountingPercentuali() {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);

  // globali: % articoli + fee €
  const [globalCfg, setGlobalCfg] = useState({
    perc_articoli: "",
    flat_fee_eur: "",
  });
  const [savingGlobal, setSavingGlobal] = useState(false);

  // override per fornitore (tabella di sotto)
  const [fornitori, setFornitori] = useState([]);
  const [savingByFornitore, setSavingByFornitore] = useState({});

  // lista completa fornitori (per il picker)
  const [allFornitori, setAllFornitori] = useState([]);
  const [fornSearch, setFornSearch] = useState("");
  const [fornLoading, setFornLoading] = useState(false);

  // batch override: valori da applicare + selezione fornitori
  const [batchOverride, setBatchOverride] = useState({
    perc_articoli: "",
    flat_fee_eur: "",
    selectedIds: [],
  });
  const [savingBatch, setSavingBatch] = useState(false);

  // prodotti rosa (cross-sell)
  const [rosaItems, setRosaItems] = useState([]);
  const [savingRosa, setSavingRosa] = useState({});

  // mappa per capire chi ha già override
  const overrideMap = useMemo(() => {
    const m = new Map();
    fornitori.forEach((f) => {
      m.set(Number(f.fornitore_id), true);
    });
    return m;
  }, [fornitori]);

  const filteredFornitori = useMemo(() => {
    const q = fornSearch.trim().toLowerCase();
    if (!q) return allFornitori;
    return allFornitori.filter((f) =>
      String(f.label || "").toLowerCase().includes(q)
    );
  }, [allFornitori, fornSearch]);

  const selectedCount = batchOverride.selectedIds.length;

  async function loadData() {
    setLoading(true);
    setFornLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [pRes, rRes, fResRaw] = await Promise.all([
        fetch(`${API_BASE}/api/admin/accounting/percentuali`, {
          headers,
          credentials: "include",
        }),
        fetch(`${API_BASE}/api/admin/accounting/rosa`, {
          headers,
          credentials: "include",
        }),
        fetch(`${API_BASE}/api/admin/accounting/fornitori-options`, {
          headers,
          credentials: "include",
        }).catch(() => null),
      ]);

      // ---------- global + overrides ----------
      const pdata = await pRes.json();
      if (!pRes.ok) {
        throw new Error(pdata?.error || `HTTP ${pRes.status}`);
      }

      const g = pdata.global || {};
      setGlobalCfg({
        perc_articoli: g.perc_articoli != null ? String(g.perc_articoli) : "",
        flat_fee_eur:
          g.flat_fee_cents != null ? toStrEurFromCents(g.flat_fee_cents) : "",
      });

      const overrideList = Array.isArray(pdata.fornitori) ? pdata.fornitori : [];
      setFornitori(
        overrideList.map((f) => ({
          ...f,
          perc_articoli: f.perc_articoli != null ? String(f.perc_articoli) : "",
          flat_fee_eur:
            f.flat_fee_cents != null ? toStrEurFromCents(f.flat_fee_cents) : "",
        }))
      );

      // ---------- rosa ----------
      const rdata = await rRes.json();
      if (!rRes.ok) {
        throw new Error(rdata?.error || `HTTP ${rRes.status}`);
      }
      const items = Array.isArray(rdata.items) ? rdata.items : [];
      setRosaItems(
        items.map((it) => ({
          ...it,
          share_percent:
            it.share_percent != null ? String(it.share_percent) : "",
        }))
      );

      // ---------- lista completa fornitori per picker ----------
      let fornList = [];

      if (fResRaw) {
        let fResData = null;
        try {
          fResData = await fResRaw.json();
        } catch {
          fResData = null;
        }

        if (fResRaw.ok && Array.isArray(fResData)) {
          fornList = fResData;
        }
      }

      setAllFornitori(
        (fornList || [])
          .map((f) => ({
            id: Number(f.id),
            label: f.label || `Fornitore #${f.id}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );

      setBatchOverride((prev) => ({
        ...prev,
        selectedIds: [],
      }));
    } catch (err) {
      console.error("[AdminAccountingPercentuali] load error:", err);
      toast.error(err?.message || "Errore caricamento configurazione");
    } finally {
      setLoading(false);
      setFornLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- globali ----------
  async function saveGlobal() {
    try {
      setSavingGlobal(true);
      const body = {
        perc_articoli:
          globalCfg.perc_articoli === ""
            ? null
            : Number(globalCfg.perc_articoli),
        flat_fee_eur:
          globalCfg.flat_fee_eur === "" ? null : Number(globalCfg.flat_fee_eur),
      };

      const r = await fetch(
        `${API_BASE}/api/admin/accounting/percentuali/global`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify(body),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.error || `HTTP ${r.status}`);
      }
      toast.success("Percentuali globali salvate");
      loadData();
    } catch (err) {
      console.error("[saveGlobal] error:", err);
      toast.error(err?.message || "Errore salvataggio globali");
    } finally {
      setSavingGlobal(false);
    }
  }

  // ---------- override singolo fornitore ----------
  async function saveFornitore(fid) {
    const f = fornitori.find((x) => x.fornitore_id === fid);
    if (!f) return;
    try {
      setSavingByFornitore((m) => ({ ...m, [fid]: true }));
      const body = {
        perc_articoli: f.perc_articoli === "" ? null : Number(f.perc_articoli),
        flat_fee_eur: f.flat_fee_eur === "" ? null : Number(f.flat_fee_eur),
      };
      const r = await fetch(
        `${API_BASE}/api/admin/accounting/percentuali/fornitore/${fid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify(body),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.error || `HTTP ${r.status}`);
      }
      toast.success(`Percentuali fornitore #${fid} salvate`);
      loadData();
    } catch (err) {
      console.error("[saveFornitore] error:", err);
      toast.error(err?.message || "Errore salvataggio fornitore");
    } finally {
      setSavingByFornitore((m) => {
        const { [fid]: _drop, ...rest } = m;
        return rest;
      });
    }
  }

  async function deleteFornitoreRule(fid) {
    try {
      setSavingByFornitore((m) => ({ ...m, [fid]: true }));
      const r = await fetch(
        `${API_BASE}/api/admin/accounting/percentuali/fornitore/${fid}`,
        {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        }
      );
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.error || `HTTP ${r.status}`);
      }
      toast.success(`Override fornitore #${fid} rimosso`);
      loadData();
    } catch (err) {
      console.error("[deleteFornitoreRule] error:", err);
      toast.error(err?.message || "Errore eliminazione override");
    } finally {
      setSavingByFornitore((m) => {
        const { [fid]: _drop, ...rest } = m;
        return rest;
      });
    }
  }

  // ---------- batch override ----------
  function toggleSupplierInBatch(fid) {
    if (overrideMap.has(fid)) return;

    setBatchOverride((b) => {
      const selected = b.selectedIds.includes(fid)
        ? b.selectedIds.filter((id) => id !== fid)
        : [...b.selectedIds, fid];
      return { ...b, selectedIds: selected };
    });
  }

  async function applyBatchOverride() {
    const ids = batchOverride.selectedIds;
    if (!ids.length) {
      toast.error("Seleziona almeno un fornitore");
      return;
    }

    const body = {
      perc_articoli:
        batchOverride.perc_articoli === ""
          ? null
          : Number(batchOverride.perc_articoli),
      flat_fee_eur:
        batchOverride.flat_fee_eur === ""
          ? null
          : Number(batchOverride.flat_fee_eur),
    };

    if (body.perc_articoli === null && body.flat_fee_eur === null) {
      toast.error("Imposta almeno un valore (% o €)");
      return;
    }

    try {
      setSavingBatch(true);
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      await Promise.all(
        ids.map((fid) =>
          fetch(
            `${API_BASE}/api/admin/accounting/percentuali/fornitore/${fid}`,
            {
              method: "PUT",
              headers,
              credentials: "include",
              body: JSON.stringify(body),
            }
          ).then(async (r) => {
            if (!r.ok) {
              const d = await r.json().catch(() => ({}));
              throw new Error(d?.error || `HTTP ${r.status}`);
            }
          })
        )
      );

      toast.success(
        ids.length === 1
          ? "Override applicato al fornitore selezionato"
          : `Override applicato a ${ids.length} fornitori`
      );
      loadData();
    } catch (err) {
      console.error("[applyBatchOverride] error:", err);
      toast.error(err?.message || "Errore creazione override");
    } finally {
      setSavingBatch(false);
    }
  }

  // ---------- prodotti rosa ----------
  async function saveRosaItem(itemId) {
    const item = rosaItems.find((x) => x.id === itemId);
    if (!item) return;
    try {
      setSavingRosa((m) => ({ ...m, [itemId]: true }));
      const body = {
        share_percent:
          item.share_percent === "" ? null : Number(item.share_percent),
      };
      const r = await fetch(`${API_BASE}/api/admin/accounting/rosa/${itemId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.error || `HTTP ${r.status}`);
      }
      toast.success(`Percentuale rosa aggiornata (item #${itemId})`);
      loadData();
    } catch (err) {
      console.error("[saveRosaItem] error:", err);
      toast.error(err?.message || "Errore salvataggio rosa");
    } finally {
      setSavingRosa((m) => {
        const { [itemId]: _drop, ...rest } = m;
        return rest;
      });
    }
  }

  return (
    <div className="acc-pct-page">
      <div className="acc-pct-inner">
        <header className="acc-pct-header">
          <div>
            <h1>Contabilità · Percentuali</h1>
            <p className="acc-pct-header-sub">
              Configura quota servizi, commissioni fisse e share dei prodotti rosa.
            </p>
          </div>
          <button
            className="acc-pct-btn acc-pct-btn-primary"
            onClick={loadData}
            disabled={loading}
            type="button"
          >
            {loading ? "Aggiornamento..." : "Ricarica"}
          </button>
        </header>

        {/* GLOBALI */}
        <section className="acc-pct-section">
          <div className="acc-pct-card">
            <div className="acc-pct-card-head">
              <div>
                <h2>Impostazioni globali</h2>
                <p>Valgono per tutti i fornitori, salvo override specifici.</p>
              </div>
              <button
                className="acc-pct-btn acc-pct-btn-primary"
                disabled={savingGlobal || loading}
                onClick={saveGlobal}
                type="button"
              >
                {savingGlobal ? "Salvataggio..." : "Salva globali"}
              </button>
            </div>

            <div className="acc-pct-global-grid">
              <div className="acc-pct-field-card">
                <div className="acc-pct-field-top">
                  <span className="acc-pct-field-label">% servizi / articoli</span>
                  <span className="acc-pct-badge">Globale</span>
                </div>
                <div className="acc-pct-input-wrap">
                  <input
                    className="acc-pct-input acc-pct-input-number"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={globalCfg.perc_articoli}
                    onChange={(e) =>
                      setGlobalCfg((g) => ({
                        ...g,
                        perc_articoli: toStrPercent(e.target.value),
                      }))
                    }
                  />
                  <span className="acc-pct-input-suffix">%</span>
                </div>
              </div>

              <div className="acc-pct-field-card">
                <div className="acc-pct-field-top">
                  <span className="acc-pct-field-label">Commissione fissa per ordine</span>
                  <span className="acc-pct-badge">Globale</span>
                </div>
                <div className="acc-pct-input-wrap">
                  <input
                    className="acc-pct-input acc-pct-input-number"
                    type="number"
                    min="0"
                    step="0.01"
                    value={globalCfg.flat_fee_eur}
                    onChange={(e) =>
                      setGlobalCfg((g) => ({
                        ...g,
                        flat_fee_eur: e.target.value,
                      }))
                    }
                  />
                  <span className="acc-pct-input-suffix">€</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OVERRIDE */}
        <section className="acc-pct-section">
          <div className="acc-pct-card">
            <div className="acc-pct-card-head">
              <div>
                <h2>Override per fornitore</h2>
                <p>Crea nuove regole oppure modifica quelle già esistenti.</p>
              </div>
            </div>

            <div className="acc-pct-batch-panel">
              <div className="acc-pct-batch-top">
                <div className="acc-pct-batch-title-wrap">
                  <div className="acc-pct-batch-title">Nuova regola</div>
                  <div className="acc-pct-batch-sub">
                    Seleziona uno o più fornitori senza override e applica i valori.
                  </div>
                </div>

                <div className="acc-pct-batch-controls">
                  <div className="acc-pct-input-wrap acc-pct-input-wrap-compact">
                    <input
                      className="acc-pct-input acc-pct-input-compact"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="% articoli"
                      value={batchOverride.perc_articoli}
                      onChange={(e) =>
                        setBatchOverride((b) => ({
                          ...b,
                          perc_articoli: toStrPercent(e.target.value),
                        }))
                      }
                    />
                    <span className="acc-pct-input-suffix">%</span>
                  </div>

                  <div className="acc-pct-input-wrap acc-pct-input-wrap-compact">
                    <input
                      className="acc-pct-input acc-pct-input-compact"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="€ fisso"
                      value={batchOverride.flat_fee_eur}
                      onChange={(e) =>
                        setBatchOverride((b) => ({
                          ...b,
                          flat_fee_eur: e.target.value,
                        }))
                      }
                    />
                    <span className="acc-pct-input-suffix">€</span>
                  </div>

                  <button
                    className="acc-pct-btn acc-pct-btn-primary"
                    onClick={applyBatchOverride}
                    disabled={savingBatch || !selectedCount}
                    type="button"
                  >
                    {savingBatch
                      ? "Applicazione..."
                      : selectedCount
                        ? `Applica a ${selectedCount}`
                        : "Applica"}
                  </button>
                </div>
              </div>

              <div className="acc-pct-supplier-box">
                <div className="acc-pct-supplier-head">
                  <input
                    className="acc-pct-input acc-pct-input-search"
                    type="search"
                    placeholder="Cerca fornitore per nome o ID..."
                    value={fornSearch}
                    onChange={(e) => setFornSearch(e.target.value)}
                  />
                  <div className="acc-pct-supplier-meta">
                    {selectedCount
                      ? `${selectedCount} selezionat${selectedCount === 1 ? "o" : "i"}`
                      : "Nessun fornitore selezionato"}
                  </div>
                </div>

                <div className="acc-pct-supplier-list">
                  {fornLoading && (
                    <div className="acc-pct-empty-inline">Caricamento fornitori...</div>
                  )}

                  {!fornLoading && filteredFornitori.length === 0 && (
                    <div className="acc-pct-empty-inline">Nessun fornitore trovato.</div>
                  )}

                  {!fornLoading &&
                    filteredFornitori.length > 0 &&
                    filteredFornitori.map((f) => {
                      const fid = f.id;
                      const hasOverride = overrideMap.has(fid);
                      const checked = batchOverride.selectedIds.includes(fid);

                      return (
                        <label
                          key={fid}
                          className={`acc-pct-supplier-option${checked ? " is-selected" : ""
                            }${hasOverride ? " is-disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            className="acc-pct-supplier-checkbox"
                            disabled={hasOverride}
                            checked={checked}
                            onChange={() => toggleSupplierInBatch(fid)}
                          />

                          <div className="acc-pct-supplier-main">
                            <span className="acc-pct-supplier-name">{f.label}</span>
                            <span className="acc-pct-supplier-id">#{fid}</span>
                          </div>

                          {hasOverride && (
                            <span className="acc-pct-chip acc-pct-chip-warning">
                              Override attivo
                            </span>
                          )}
                        </label>
                      );
                    })}
                </div>
              </div>
            </div>

            <div className="acc-pct-table-wrap">
              <table className="acc-pct-table">
                <thead>
                  <tr>
                    <th>Fornitore</th>
                    <th className="is-right">% articoli</th>
                    <th className="is-right">€ fisso</th>
                    <th className="is-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="4" className="acc-pct-table-empty">
                        Caricamento override...
                      </td>
                    </tr>
                  )}

                  {!loading && fornitori.length === 0 && (
                    <tr>
                      <td colSpan="4" className="acc-pct-table-empty">
                        Nessun override per fornitore.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    fornitori.length > 0 &&
                    fornitori.map((f) => {
                      const fid = f.fornitore_id;
                      const busy = !!savingByFornitore[fid];

                      return (
                        <tr key={fid}>
                          <td>
                            <div className="acc-pct-table-name">
                              <span>{f.nome}</span>
                              <span className="acc-pct-table-id">#{fid}</span>
                            </div>
                          </td>

                          <td className="is-right">
                            <div className="acc-pct-input-wrap acc-pct-input-wrap-inline">
                              <input
                                className="acc-pct-input acc-pct-input-number"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={f.perc_articoli}
                                onChange={(e) =>
                                  setFornitori((list) =>
                                    list.map((x) =>
                                      x.fornitore_id === fid
                                        ? {
                                          ...x,
                                          perc_articoli: toStrPercent(
                                            e.target.value
                                          ),
                                        }
                                        : x
                                    )
                                  )
                                }
                                disabled={busy}
                              />
                              <span className="acc-pct-input-suffix">%</span>
                            </div>
                          </td>

                          <td className="is-right">
                            <div className="acc-pct-input-wrap acc-pct-input-wrap-inline">
                              <input
                                className="acc-pct-input acc-pct-input-number"
                                type="number"
                                min="0"
                                step="0.01"
                                value={f.flat_fee_eur}
                                onChange={(e) =>
                                  setFornitori((list) =>
                                    list.map((x) =>
                                      x.fornitore_id === fid
                                        ? {
                                          ...x,
                                          flat_fee_eur: e.target.value,
                                        }
                                        : x
                                    )
                                  )
                                }
                                disabled={busy}
                              />
                              <span className="acc-pct-input-suffix">€</span>
                            </div>
                          </td>

                          <td className="is-right">
                            <div className="acc-pct-actions">
                              <button
                                className="acc-pct-btn acc-pct-btn-ghost"
                                disabled={busy}
                                onClick={() => saveFornitore(fid)}
                                type="button"
                              >
                                {busy ? "..." : "Salva"}
                              </button>
                              <button
                                className="acc-pct-btn acc-pct-btn-danger"
                                disabled={busy}
                                onClick={() => deleteFornitoreRule(fid)}
                                type="button"
                              >
                                Rimuovi
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ROSA */}
        <section className="acc-pct-section">
          <div className="acc-pct-card">
            <div className="acc-pct-card-head">
              <div>
                <h2>Prodotti rosa (cross-sell)</h2>
                <p>
                  Percentuale del nostro share su ogni item rosa
                  <code className="acc-pct-code">share_percent</code>.
                </p>
              </div>
            </div>

            <div className="acc-pct-table-wrap">
              <table className="acc-pct-table">
                <thead>
                  <tr>
                    <th>Set / Item</th>
                    <th>Tipo</th>
                    <th className="is-right">% rosa</th>
                    <th className="is-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="4" className="acc-pct-table-empty">
                        Caricamento prodotti rosa...
                      </td>
                    </tr>
                  )}

                  {!loading && rosaItems.length === 0 && (
                    <tr>
                      <td colSpan="4" className="acc-pct-table-empty">
                        Nessun prodotto rosa configurato.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    rosaItems.length > 0 &&
                    rosaItems.map((it) => {
                      const busy = !!savingRosa[it.id];
                      const label =
                        it.custom_title || it.service_name || `Item #${it.id}`;
                      const tipo = it.custom_title
                        ? "Custom"
                        : it.service_name
                          ? "Servizio"
                          : "Altro";

                      return (
                        <tr key={it.id}>
                          <td>
                            <div className="acc-pct-item-cell">
                              <span className="acc-pct-item-label">{label}</span>
                              <span className="acc-pct-chip">Set #{it.set_id}</span>
                            </div>
                          </td>

                          <td>{tipo}</td>

                          <td className="is-right">
                            <div className="acc-pct-input-wrap acc-pct-input-wrap-inline">
                              <input
                                className="acc-pct-input acc-pct-input-number"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={it.share_percent}
                                onChange={(e) =>
                                  setRosaItems((list) =>
                                    list.map((x) =>
                                      x.id === it.id
                                        ? {
                                          ...x,
                                          share_percent: toStrPercent(
                                            e.target.value
                                          ),
                                        }
                                        : x
                                    )
                                  )
                                }
                                disabled={busy}
                              />
                              <span className="acc-pct-input-suffix">%</span>
                            </div>
                          </td>

                          <td className="is-right">
                            <button
                              className="acc-pct-btn acc-pct-btn-ghost"
                              disabled={busy}
                              onClick={() => saveRosaItem(it.id)}
                              type="button"
                            >
                              {busy ? "..." : "Salva"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}